import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Chip,
  useTheme,
  Badge,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  Settings as SettingsIcon,
  Videocam as VideocamIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Repeat as RepeatIcon,
  NotificationsActive as NotificationsActiveIcon,
  Room as RoomIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { format, parse, startOfWeek, getDay, addMinutes } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { consultasIniciais, pacientesIniciais } from '../shared/initialData';

// Configuração do localizador para o calendário
const locales = {
  'pt-BR': ptBR
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

// Componente principal da Agenda
const AgendaPsicologo = () => {
  const [events, setEvents] = useState([]);
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [psicologoId, setPsicologoId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [calendarView, setCalendarView] = useState('week');
  
  // Horários de funcionamento do psicólogo
  const [horariosFuncionamento, setHorariosFuncionamento] = useState({
    segunda: { ativo: true, inicio: '08:00', fim: '18:00' },
    terca: { ativo: true, inicio: '08:00', fim: '18:00' },
    quarta: { ativo: true, inicio: '08:00', fim: '18:00' },
    quinta: { ativo: true, inicio: '08:00', fim: '18:00' },
    sexta: { ativo: true, inicio: '08:00', fim: '18:00' },
    sabado: { ativo: false, inicio: '08:00', fim: '12:00' },
    domingo: { ativo: false, inicio: '00:00', fim: '00:00' }
  });
  
  // Dias bloqueados (férias, feriados, etc.)
  const [diasBloqueados, setDiasBloqueados] = useState([]);
  
  // Duração padrão das consultas
  const [duracaoPadrao, setDuracaoPadrao] = useState(50);
  
  // Formulário para novo evento/consulta
  const [formData, setFormData] = useState({
    title: '',
    pacienteId: '',
    start: null,
    end: null,
    tipo: 'Presencial',
    plataforma: '',
    status: 'Agendada',
    observacoes: '',
    bloqueado: false,
    motivoBloqueio: '',
    novoDiaBloqueado: '',
    periodoInicioBloqueioDia: '',
    periodoFimBloqueioDia: '',
    motivoBloqueioIntervalo: '',
    isRecorrente: false,
    diaSemanaRecorrente: '',
    horarioRecorrente: '',
    duracaoRecorrente: 50,
    dataFimRecorrencia: '',
    tipoRecorrencia: 'semanal',
    intervaloRecorrencia: 1
  });

  const theme = useTheme(); // Usar o tema do Material-UI

  // Carrega dados iniciais
  useEffect(() => {
    // Verifica se o usuário é psicólogo
    const tipoUsuario = localStorage.getItem('userType');
    if (tipoUsuario !== 'psicologo') {
      return;
    }

    const storedPsicologoId = localStorage.getItem('userId');
    if (storedPsicologoId) {
      setPsicologoId(parseInt(storedPsicologoId));
      
      // Filtra consultas do psicólogo
      const consultasFiltradas = consultasIniciais.filter(
        consulta => consulta.psicologoId === parseInt(storedPsicologoId)
      );
      
      // Converte consultas para eventos do calendário
      const eventosConvertidos = consultasFiltradas.map(consulta => {
        const dataHoraInicio = parseDateTime(consulta.data, consulta.horario);
        const dataHoraFim = addMinutes(dataHoraInicio, parseInt(consulta.duracao));
        
        return {
          id: consulta.id,
          title: getPacienteNome(consulta.pacienteId, pacientesIniciais),
          pacienteId: consulta.pacienteId,
          start: dataHoraInicio,
          end: dataHoraFim,
          tipo: consulta.tipo,
          plataforma: consulta.plataforma,
          status: consulta.status,
          observacoes: consulta.observacoes,
          bloqueado: false,
          resource: { status: consulta.status }
        };
      });
      
      setEvents(eventosConvertidos);
      
      // Filtra pacientes do psicólogo
      const pacientesFiltrados = pacientesIniciais.filter(
        paciente => paciente.psicologoId === parseInt(storedPsicologoId)
      );
      setPacientes(pacientesFiltrados);
    }
  }, []);

  // Função para converter string de data e hora para objeto Date
  const parseDateTime = (dataStr, horaStr) => {
    const [dia, mes, ano] = dataStr.split('/');
    const [hora, minuto] = horaStr.split(':');
    return new Date(ano, mes - 1, dia, hora, minuto);
  };

  // Obtém o nome do paciente pelo ID
  const getPacienteNome = (pacienteId, listaPacientes) => {
    const paciente = listaPacientes.find(p => p.id === pacienteId);
    return paciente ? paciente.nome : 'Paciente não encontrado';
  };

  // Manipuladores para as abas
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Manipuladores para o calendário
  const handleSelectSlot = ({ start, end }) => {
    // Verifica se o dia está bloqueado
    const dataFormatada = format(start, 'yyyy-MM-dd');
    if (diasBloqueados.includes(dataFormatada)) {
      alert('Este dia está bloqueado para agendamentos.');
      return;
    }
    
    // Verifica se está dentro do horário de funcionamento
    const diaSemana = getDayName(start.getDay());
    const horarioFunc = horariosFuncionamento[diaSemana];
    
    if (!horarioFunc.ativo) {
      alert('Não há atendimento neste dia da semana.');
      return;
    }
    
    const horaInicio = start.getHours() + ':' + (start.getMinutes() < 10 ? '0' : '') + start.getMinutes();
    if (horaInicio < horarioFunc.inicio || horaInicio >= horarioFunc.fim) {
      alert('Fora do horário de atendimento.');
      return;
    }
    
    setFormData({
      title: '',
      pacienteId: '',
      start,
      end,
      tipo: 'Presencial',
      plataforma: '',
      status: 'Agendada',
      observacoes: '',
      bloqueado: false,
      motivoBloqueio: '',
      novoDiaBloqueado: '',
      periodoInicioBloqueioDia: '',
      periodoFimBloqueioDia: '',
      motivoBloqueioIntervalo: '',
      isRecorrente: false,
      diaSemanaRecorrente: '',
      horarioRecorrente: '',
      duracaoRecorrente: 50,
      dataFimRecorrencia: '',
      tipoRecorrencia: 'semanal',
      intervaloRecorrencia: 1
    });
    setCurrentEvent(null);
    setOpenEventDialog(true);
  };

  const handleSelectEvent = (event) => {
    setCurrentEvent(event);
    setFormData({
      title: event.title,
      pacienteId: event.pacienteId,
      start: event.start,
      end: event.end,
      tipo: event.tipo,
      plataforma: event.plataforma,
      status: event.status,
      observacoes: event.observacoes,
      bloqueado: event.bloqueado,
      motivoBloqueio: event.motivoBloqueio,
      novoDiaBloqueado: '',
      periodoInicioBloqueioDia: '',
      periodoFimBloqueioDia: '',
      motivoBloqueioIntervalo: '',
      isRecorrente: false,
      diaSemanaRecorrente: '',
      horarioRecorrente: '',
      duracaoRecorrente: 50,
      dataFimRecorrencia: '',
      tipoRecorrencia: 'semanal',
      intervaloRecorrencia: 1
    });
    setOpenEventDialog(true);
  };

  // Manipuladores para os diálogos
  const handleCloseEventDialog = () => {
    setOpenEventDialog(false);
    setFormErrors({});
  };

  const handleOpenConfigDialog = () => {
    setOpenConfigDialog(true);
  };

  const handleCloseConfigDialog = () => {
    setOpenConfigDialog(false);
  };

  // Manipuladores de formulário
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Limpa o erro quando o usuário corrige o campo
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: null
      });
    }
  };

  const handleHorarioChange = (dia, campo, valor) => {
    setHorariosFuncionamento({
      ...horariosFuncionamento,
      [dia]: {
        ...horariosFuncionamento[dia],
        [campo]: valor
      }
    });
  };

  const handleDiaAtivoChange = (dia, valor) => {
    setHorariosFuncionamento({
      ...horariosFuncionamento,
      [dia]: {
        ...horariosFuncionamento[dia],
        ativo: valor
      }
    });
  };

  const handleAddDiaBloqueado = (novoBloqueioDia) => {
    if (!diasBloqueados.includes(novoBloqueioDia)) {
      setDiasBloqueados([...diasBloqueados, novoBloqueioDia]);
    }
  };

  const handleRemoveDiaBloqueado = (dia) => {
    setDiasBloqueados(diasBloqueados.filter(d => d !== dia));
  };

  const handleBloqueioToggle = (bloqueado) => {
    setFormData({
      ...formData,
      bloqueado,
      pacienteId: bloqueado ? '' : formData.pacienteId,
      title: bloqueado ? 'Bloqueado' : formData.title
    });
  };

  // Adicionar função para adicionar intervalo de datas bloqueadas
  const handleAddPeriodoBloqueado = () => {
    if (!formData.periodoInicioBloqueioDia || !formData.periodoFimBloqueioDia) {
      return;
    }

    const dataInicio = new Date(formData.periodoInicioBloqueioDia);
    const dataFim = new Date(formData.periodoFimBloqueioDia);

    // Verifica se a data de início é anterior à data de fim
    if (dataInicio > dataFim) {
      alert('A data de início deve ser anterior à data de fim.');
      return;
    }

    // Gera um array com todas as datas no intervalo
    const datasNoIntervalo = [];
    const dataAtual = new Date(dataInicio);
    
    while (dataAtual <= dataFim) {
      const dataFormatada = format(dataAtual, 'yyyy-MM-dd');
      if (!diasBloqueados.includes(dataFormatada)) {
        datasNoIntervalo.push(dataFormatada);
      }
      dataAtual.setDate(dataAtual.getDate() + 1);
    }

    // Adiciona todas as datas ao array de dias bloqueados
    setDiasBloqueados([...diasBloqueados, ...datasNoIntervalo]);

    // Limpa os campos após adicionar o intervalo
    handleInputChange('periodoInicioBloqueioDia', '');
    handleInputChange('periodoFimBloqueioDia', '');
    handleInputChange('motivoBloqueioIntervalo', '');
  };

  // Validação e salvamento
  const validateForm = () => {
    const errors = {};
    
    if (!formData.bloqueado && !formData.pacienteId) {
      errors.pacienteId = 'Selecione um paciente';
    }
    
    if (formData.bloqueado && !formData.motivoBloqueio) {
      errors.motivoBloqueio = 'Informe o motivo do bloqueio';
    }
    
    if (formData.tipo === 'Online' && !formData.plataforma) {
      errors.plataforma = 'Informe a plataforma para consulta online';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEvent = () => {
    if (!validateForm()) return;
    
    const newEvent = {
      id: currentEvent ? currentEvent.id : Math.max(...events.map(e => e.id || 0), 0) + 1,
      title: formData.bloqueado ? `BLOQUEADO: ${formData.motivoBloqueio}` : getPacienteNome(parseInt(formData.pacienteId), pacientes),
      pacienteId: formData.bloqueado ? null : parseInt(formData.pacienteId),
      start: formData.start,
      end: formData.end,
      tipo: formData.tipo,
      plataforma: formData.plataforma,
      status: formData.status,
      observacoes: formData.observacoes,
      bloqueado: formData.bloqueado,
      motivoBloqueio: formData.motivoBloqueio,
      resource: { status: formData.status, bloqueado: formData.bloqueado }
    };
    
    if (currentEvent) {
      // Atualizar evento existente
      setEvents(events.map(e => e.id === currentEvent.id ? newEvent : e));
    } else {
      // Criar novo evento
      setEvents([...events, newEvent]);
    }
    
    handleCloseEventDialog();
  };

  const handleDeleteEvent = () => {
    if (currentEvent && window.confirm('Tem certeza que deseja excluir este evento?')) {
      setEvents(events.filter(e => e.id !== currentEvent.id));
      handleCloseEventDialog();
    }
  };

  const handleSaveConfig = () => {
    // Salvar configurações
    handleCloseConfigDialog();
    // Aqui você poderia enviar as configurações para o servidor
  };

  // Funções auxiliares
  const getDayName = (dayIndex) => {
    const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
    return days[dayIndex];
  };

  const translateDayName = (day) => {
    const translations = {
      'domingo': 'Domingo',
      'segunda': 'Segunda-feira',
      'terca': 'Terça-feira',
      'quarta': 'Quarta-feira',
      'quinta': 'Quinta-feira',
      'sexta': 'Sexta-feira',
      'sabado': 'Sábado'
    };
    return translations[day] || day;
  };

  // Estilizações para o calendário
  const eventStyleGetter = (event) => {
    let backgroundColor = '#4a8a84';
    let textColor = 'white';
    let borderColor = '#3a7a74';
    let borderWidth = '0px';
    let borderStyle = 'solid';
    let opacity = 1;
    let borderRadius = '6px';
    let boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    let fontWeight = 'normal';
    let icons = null;
    
    if (event.bloqueado) {
      backgroundColor = '#f44336';
      borderColor = '#d32f2f';
      textColor = 'white';
    } else if (event.resource && event.resource.status) {
      switch (event.resource.status) {
        case 'Agendada':
          backgroundColor = '#4a8a84';
          borderColor = '#3a7a74';
          break;
        case 'Realizada':
          backgroundColor = '#4caf50';
          borderColor = '#388e3c';
          break;
        case 'Cancelada':
          backgroundColor = '#9e9e9e';
          borderColor = '#757575';
          textColor = '#f5f5f5';
          opacity = 0.8;
          break;
        case 'Remarcada':
          backgroundColor = '#ff9800';
          borderColor = '#f57c00';
          break;
        default:
          backgroundColor = '#4a8a84';
      }
    }
    
    // Destaque para eventos recorrentes
    if (event.isRecorrente) {
      borderWidth = '2px';
      borderStyle = 'dashed';
      fontWeight = '500';
    }
    
    return {
      style: {
        backgroundColor,
        color: textColor,
        borderRadius,
        border: `${borderWidth} ${borderStyle} ${borderColor}`,
        padding: '4px 8px',
        fontSize: '0.875rem',
        opacity,
        boxShadow,
        fontWeight,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.15)'
        }
      }
    };
  };

  const dayPropGetter = (date) => {
    const dataFormatada = format(date, 'yyyy-MM-dd');
    const diaSemana = getDayName(date.getDay());
    const horarioFunc = horariosFuncionamento[diaSemana];
    const isToday = new Date().toDateString() === date.toDateString();
    
    if (!horarioFunc.ativo || diasBloqueados.includes(dataFormatada)) {
      return {
        style: {
          backgroundColor: '#f5f5f5',
          opacity: 0.7,
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(200,200,200,0.2) 10px, rgba(200,200,200,0.2) 20px)'
        },
        className: 'dia-bloqueado'
      };
    }
    
    if (isToday) {
      return {
        style: {
          backgroundColor: 'rgba(74, 138, 132, 0.1)', // Cor do tema com baixa opacidade
          borderLeft: '4px solid #4a8a84'
        },
        className: 'dia-atual'
      };
    }
    
    return {};
  };

  // Componente personalizado para renderizar o conteúdo do evento
  const EventComponent = ({ event }) => {
    let icon = null;
    
    if (event.bloqueado) {
      icon = <BlockIcon fontSize="small" style={{ marginRight: '4px' }} />;
    } else {
      switch (event.tipo) {
        case 'Online':
          icon = <VideocamIcon fontSize="small" style={{ marginRight: '4px' }} />;
          break;
        case 'Presencial':
          icon = <PersonIcon fontSize="small" style={{ marginRight: '4px' }} />;
          break;
        case 'Domiciliar':
          icon = <HomeIcon fontSize="small" style={{ marginRight: '4px' }} />;
          break;
        default:
          icon = <EventIcon fontSize="small" style={{ marginRight: '4px' }} />;
      }
    }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
        {icon}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {event.title}
        </span>
        {event.isRecorrente && (
          <RepeatIcon fontSize="small" style={{ marginLeft: '4px', opacity: 0.7 }} />
        )}
      </div>
    );
  };

  // Adicionar função para criar consultas recorrentes
  const handleSaveRecorrente = () => {
    if (!validateRecorrenteForm()) return;
    
    const pacienteId = parseInt(formData.pacienteId);
    const paciente = pacientes.find(p => p.id === pacienteId);
    if (!paciente) {
      alert('Paciente não encontrado.');
      return;
    }
    
    // Dias da semana com índice numérico
    const diasSemanaIndices = {
      'domingo': 0,
      'segunda': 1,
      'terca': 2,
      'quarta': 3,
      'quinta': 4,
      'sexta': 5,
      'sabado': 6
    };
    
    const diaSemanaIndex = diasSemanaIndices[formData.diaSemanaRecorrente];
    if (diaSemanaIndex === undefined) {
      alert('Dia da semana inválido.');
      return;
    }
    
    // Parse do horário
    const [horas, minutos] = formData.horarioRecorrente.split(':').map(Number);
    
    // Cria a data de início para o próximo dia da semana correspondente
    const dataInicio = new Date();
    const dataFimRecorrencia = formData.dataFimRecorrencia ? new Date(formData.dataFimRecorrencia) : null;
    
    // Ajusta para o próximo dia da semana correspondente
    dataInicio.setHours(horas, minutos, 0, 0);
    const diff = (diaSemanaIndex + 7 - dataInicio.getDay()) % 7;
    dataInicio.setDate(dataInicio.getDate() + (diff === 0 ? 7 : diff)); // Se for hoje, vai para a próxima semana
    
    // Gera os eventos recorrentes até a data limite (ou 6 meses no máximo)
    const seisMesesDepois = new Date();
    seisMesesDepois.setMonth(seisMesesDepois.getMonth() + 6);
    
    const dataLimite = dataFimRecorrencia || seisMesesDepois;
    const eventosRecorrentes = [];
    const dataAtual = new Date(dataInicio);
    
    let idBase = Math.max(...events.map(e => e.id || 0), 0) + 1;
    
    // Determinando o intervalo baseado no tipo de recorrência
    let intervalo = formData.intervaloRecorrencia;
    let incrementoDias = 7; // Default para semanal
    
    switch (formData.tipoRecorrencia) {
      case 'semanal':
        incrementoDias = 7 * intervalo;
        break;
      case 'quinzenal':
        incrementoDias = 14 * intervalo;
        break;
      case 'mensal':
        // Para recorrência mensal, vamos incrementar meses em vez de dias
        // Vamos criar uma data por vez e avançar o mês
        while (dataAtual <= dataLimite) {
          // Verifica se o dia não está bloqueado
          const dataFormatada = format(dataAtual, 'yyyy-MM-dd');
          if (!diasBloqueados.includes(dataFormatada)) {
            const inicio = new Date(dataAtual);
            const fim = addMinutes(inicio, formData.duracaoRecorrente);
            
            // Cria o evento
            eventosRecorrentes.push({
              id: idBase++,
              title: paciente.nome,
              pacienteId: pacienteId,
              start: inicio,
              end: fim,
              tipo: formData.tipo,
              plataforma: formData.plataforma,
              status: 'Agendada',
              observacoes: formData.observacoes,
              bloqueado: false,
              isRecorrente: true,
              tipoRecorrencia: formData.tipoRecorrencia,
              resource: { status: 'Agendada', recorrente: true }
            });
          }
          
          // Avança para o próximo mês (considerando o intervalo)
          const mesAtual = dataAtual.getMonth();
          dataAtual.setMonth(mesAtual + intervalo);
          
          // Se mudar para um dia inválido (por exemplo, 31 de janeiro -> 31 de fevereiro não existe)
          // ajustar para o último dia do mês
          const novoDia = dataAtual.getDate();
          const diaOriginal = dataInicio.getDate();
          if (novoDia !== diaOriginal) {
            dataAtual.setDate(0); // Último dia do mês anterior
          }
        }
        
        // Adiciona os eventos recorrentes à agenda
        setEvents([...events, ...eventosRecorrentes]);
        
        let tipoRecorrenciaTexto = 'mensais';
        if (intervalo > 1) {
          tipoRecorrenciaTexto = `a cada ${intervalo} meses`;
        }
        
        alert(`Foram criadas ${eventosRecorrentes.length} consultas recorrentes para ${paciente.nome} ${tipoRecorrenciaTexto} às ${formData.horarioRecorrente}.`);
        
        handleCloseEventDialog();
        return;
    }
    
    // Para recorrência semanal e quinzenal
    while (dataAtual <= dataLimite) {
      // Verifica se o dia não está bloqueado
      const dataFormatada = format(dataAtual, 'yyyy-MM-dd');
      if (!diasBloqueados.includes(dataFormatada)) {
        const inicio = new Date(dataAtual);
        const fim = addMinutes(inicio, formData.duracaoRecorrente);
        
        // Cria o evento
        eventosRecorrentes.push({
          id: idBase++,
          title: paciente.nome,
          pacienteId: pacienteId,
          start: inicio,
          end: fim,
          tipo: formData.tipo,
          plataforma: formData.plataforma,
          status: 'Agendada',
          observacoes: formData.observacoes,
          bloqueado: false,
          isRecorrente: true,
          tipoRecorrencia: formData.tipoRecorrencia,
          resource: { status: 'Agendada', recorrente: true }
        });
      }
      
      // Avança para o próximo período conforme o incremento calculado
      dataAtual.setDate(dataAtual.getDate() + incrementoDias);
    }
    
    // Adiciona os eventos recorrentes à agenda
    setEvents([...events, ...eventosRecorrentes]);
    
    let tipoRecorrenciaTexto = '';
    switch (formData.tipoRecorrencia) {
      case 'semanal':
        tipoRecorrenciaTexto = intervalo > 1 
          ? `a cada ${intervalo} semanas às ${formData.horarioRecorrente}`
          : `todas as ${translateDayName(formData.diaSemanaRecorrente)}s às ${formData.horarioRecorrente}`;
        break;
      case 'quinzenal':
        tipoRecorrenciaTexto = intervalo > 1 
          ? `a cada ${intervalo * 2} semanas às ${formData.horarioRecorrente}`
          : `quinzenalmente às ${formData.horarioRecorrente}`;
        break;
    }
    
    alert(`Foram criadas ${eventosRecorrentes.length} consultas recorrentes para ${paciente.nome} ${tipoRecorrenciaTexto}.`);
    
    handleCloseEventDialog();
  };

  // Validação de formulário recorrente
  const validateRecorrenteForm = () => {
    const errors = {};
    
    if (!formData.pacienteId) {
      errors.pacienteId = 'Selecione um paciente';
    }
    
    if (!formData.diaSemanaRecorrente) {
      errors.diaSemanaRecorrente = 'Selecione o dia da semana';
    }
    
    if (!formData.horarioRecorrente) {
      errors.horarioRecorrente = 'Informe o horário';
    }
    
    if (formData.tipo === 'Online' && !formData.plataforma) {
      errors.plataforma = 'Informe a plataforma para consulta online';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4" sx={{ color: '#2c5652', fontWeight: 600 }}>
          Minha Agenda
        </Typography>
        
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<SettingsIcon />}
            onClick={handleOpenConfigDialog}
            sx={{ 
              mr: 2, 
              borderColor: '#4a8a84', 
              color: '#4a8a84',
              '&:hover': { 
                borderColor: '#3a7a74', 
                backgroundColor: 'rgba(74, 138, 132, 0.1)' 
              } 
            }}
          >
            Configurações
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({
                title: '',
                pacienteId: '',
                start: new Date(),
                end: addMinutes(new Date(), duracaoPadrao),
                tipo: 'Presencial',
                plataforma: '',
                status: 'Agendada',
                observacoes: '',
                bloqueado: false,
                motivoBloqueio: '',
                novoDiaBloqueado: '',
                periodoInicioBloqueioDia: '',
                periodoFimBloqueioDia: '',
                motivoBloqueioIntervalo: '',
                isRecorrente: false,
                diaSemanaRecorrente: '',
                horarioRecorrente: '',
                duracaoRecorrente: 50,
                dataFimRecorrencia: '',
                tipoRecorrencia: 'semanal',
                intervaloRecorrencia: 1
              });
              setCurrentEvent(null);
              setOpenEventDialog(true);
            }}
            sx={{ 
              bgcolor: '#4a8a84', 
              '&:hover': { bgcolor: '#3a7a74' },
              fontWeight: 500,
              boxShadow: '0 3px 5px rgba(0,0,0,0.1)'
            }}
          >
            Nova Consulta
          </Button>
        </Box>
      </Box>
      
      <Paper sx={{ 
        p: 2, 
        mb: 3, 
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
        borderRadius: 2,
        overflow: 'hidden' 
      }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Tabs
            value={calendarView}
            onChange={(e, newValue) => setCalendarView(newValue)}
            sx={{
              '& .MuiTab-root': { 
                minWidth: 100,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '1rem'
              },
              '& .Mui-selected': { color: '#4a8a84' },
              '& .MuiTabs-indicator': { backgroundColor: '#4a8a84', height: '3px' }
            }}
          >
            <Tab value="day" label="Dia" icon={<EventIcon />} iconPosition="start" />
            <Tab value="week" label="Semana" icon={<CalendarViewWeekIcon />} iconPosition="start" />
            <Tab value="month" label="Mês" icon={<CalendarViewWeekIcon />} iconPosition="start" />
          </Tabs>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              icon={<EventIcon style={{ color: '#4a8a84' }} />} 
              label={`${events.length} eventos`} 
              variant="outlined"
              sx={{ 
                borderColor: '#e0e0e0', 
                bgcolor: 'rgba(74, 138, 132, 0.08)',
                fontSize: '0.875rem'
              }}
            />
          </Box>
        </Box>
        
        <Box sx={{ 
          height: 650, 
          '.rbc-month-view': {
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
          },
          '.rbc-header': {
            padding: '8px 0',
            fontWeight: 500,
            backgroundColor: '#f8f8f8',
            borderBottom: '1px solid #e0e0e0'
          },
          '.rbc-day-bg': {
            transition: 'background-color 0.2s'
          },
          '.rbc-day-bg:hover': {
            backgroundColor: 'rgba(74, 138, 132, 0.05)'
          },
          '.rbc-off-range-bg': {
            backgroundColor: '#f9f9f9'
          },
          '.rbc-off-range': {
            color: '#bdbdbd'
          },
          '.rbc-today': {
            backgroundColor: 'rgba(74, 138, 132, 0.1)'
          },
          '.rbc-event': {
            backgroundColor: '#4a8a84',
            border: 'none',
            borderRadius: '4px'
          },
          '.rbc-toolbar button': {
            color: '#4a8a84',
            borderColor: '#e0e0e0'
          },
          '.rbc-toolbar button:hover': {
            backgroundColor: 'rgba(74, 138, 132, 0.1)',
            borderColor: '#4a8a84'
          },
          '.rbc-toolbar button.rbc-active': {
            backgroundColor: '#4a8a84',
            color: 'white',
            borderColor: '#4a8a84'
          },
          '.rbc-time-content': {
            borderTop: '2px solid #e0e0e0'
          },
          '.rbc-time-header-content': {
            borderLeft: '1px solid #e0e0e0'
          },
          '.rbc-time-view': {
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden'
          },
          '.rbc-timeslot-group': {
            borderBottom: '1px solid #f0f0f0'
          },
          '.rbc-agenda-view': {
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden'
          },
          '.rbc-agenda-table': {
            border: 'none'
          },
          '.rbc-agenda-empty': {
            padding: '20px',
            textAlign: 'center',
            color: '#757575'
          }
        }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['day', 'week', 'month']}
            view={calendarView}
            onView={(view) => setCalendarView(view)}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
            components={{
              event: EventComponent // Componente personalizado para o evento
            }}
            toolbar={true}
            messages={{
              today: 'Hoje',
              previous: 'Anterior',
              next: 'Próximo',
              month: 'Mês',
              week: 'Semana',
              day: 'Dia',
              agenda: 'Agenda',
              date: 'Data',
              time: 'Hora',
              event: 'Evento',
              noEventsInRange: 'Não há eventos neste período',
              showMore: (total) => `+ ${total} mais`
            }}
            formats={{
              dateFormat: 'dd',
              dayFormat: (date, culture, localizer) => 
                localizer.format(date, 'dd', culture) + ' ' + localizer.format(date, 'EEE', culture),
              monthHeaderFormat: (date, culture, localizer) => 
                localizer.format(date, 'MMMM yyyy', culture),
              dayHeaderFormat: (date, culture, localizer) => 
                localizer.format(date, 'EEEE, dd MMMM', culture),
              dayRangeHeaderFormat: ({ start, end }, culture, localizer) => 
                localizer.format(start, 'dd/MM/yyyy', culture) + ' - ' + 
                localizer.format(end, 'dd/MM/yyyy', culture)
            }}
            min={new Date(0, 0, 0, 7, 0, 0)}
            max={new Date(0, 0, 0, 21, 0, 0)}
            step={30} // Intervalo de 30 minutos para melhor visualização
            timeslots={2} // Mostra 2 slots por hora
            // Adicionar legenda para tipos de eventos
            onShowMore={(events, date) => console.log(events, date)}
          />
        </Box>
        
        {/* Adicionar legenda abaixo do calendário */}
        <Box sx={{ 
          mt: 2, 
          pt: 2, 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap'
        }}>
          <Chip 
            size="small"
            sx={{ bgcolor: '#4a8a84', color: 'white' }} 
            label="Agendada" 
            icon={<EventIcon style={{ color: 'white' }} />}
          />
          <Chip 
            size="small"
            sx={{ bgcolor: '#4caf50', color: 'white' }} 
            label="Realizada" 
            icon={<CheckCircleIcon style={{ color: 'white' }} />}
          />
          <Chip 
            size="small"
            sx={{ bgcolor: '#9e9e9e', color: 'white' }} 
            label="Cancelada" 
            icon={<CancelIcon style={{ color: 'white' }} />}
          />
          <Chip 
            size="small"
            sx={{ bgcolor: '#ff9800', color: 'white' }} 
            label="Remarcada" 
            icon={<EventIcon style={{ color: 'white' }} />}
          />
          <Chip 
            size="small"
            sx={{ bgcolor: '#f44336', color: 'white' }} 
            label="Bloqueado" 
            icon={<BlockIcon style={{ color: 'white' }} />}
          />
          
          <Divider orientation="vertical" flexItem />
          
          <Chip 
            size="small"
            sx={{ bgcolor: 'white', border: '2px dashed #3a7a74', color: '#3a7a74' }} 
            label="Recorrente" 
            icon={<RepeatIcon style={{ color: '#3a7a74' }} />}
          />
          <Chip 
            size="small"
            variant="outlined"
            sx={{ borderColor: '#3a7a74', color: '#3a7a74' }} 
            label="Presencial" 
            icon={<PersonIcon style={{ color: '#3a7a74' }} />}
          />
          <Chip 
            size="small"
            variant="outlined"
            sx={{ borderColor: '#3a7a74', color: '#3a7a74' }} 
            label="Online" 
            icon={<VideocamIcon style={{ color: '#3a7a74' }} />}
          />
          <Chip 
            size="small"
            variant="outlined"
            sx={{ borderColor: '#3a7a74', color: '#3a7a74' }} 
            label="Domiciliar" 
            icon={<RoomIcon style={{ color: '#3a7a74' }} />}
          />
        </Box>
      </Paper>
      
      {/* Dialog para criar/editar evento */}
      <Dialog open={openEventDialog} onClose={handleCloseEventDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentEvent ? (currentEvent.bloqueado ? 'Editar Bloqueio' : 'Editar Consulta') : 'Novo Evento'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={formData.bloqueado}
                      onChange={(e) => handleBloqueioToggle(e.target.checked)}
                      disabled={formData.isRecorrente}
                    />
                  } 
                  label="Bloquear horário"
                  sx={{ mr: 2 }}
                />
                
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={formData.isRecorrente}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          isRecorrente: e.target.checked,
                          bloqueado: e.target.checked ? false : formData.bloqueado
                        });
                      }}
                      disabled={!!currentEvent}
                    />
                  } 
                  label="Consulta recorrente"
                />
              </Box>
            </Grid>
            
            {formData.bloqueado ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Motivo do bloqueio"
                  value={formData.motivoBloqueio}
                  onChange={(e) => handleInputChange('motivoBloqueio', e.target.value)}
                  error={!!formErrors.motivoBloqueio}
                  helperText={formErrors.motivoBloqueio}
                />
              </Grid>
            ) : formData.isRecorrente ? (
              // Formulário para consultas recorrentes
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.pacienteId}>
                    <InputLabel>Paciente</InputLabel>
                    <Select
                      value={formData.pacienteId || ''}
                      label="Paciente"
                      onChange={(e) => handleInputChange('pacienteId', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Selecione um paciente</em>
                      </MenuItem>
                      {pacientes.map((paciente) => (
                        <MenuItem key={paciente.id} value={paciente.id.toString()}>
                          {paciente.nome}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.pacienteId && (
                      <Typography variant="caption" color="error">
                        {formErrors.pacienteId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Recorrência</InputLabel>
                    <Select
                      value={formData.tipoRecorrencia}
                      label="Tipo de Recorrência"
                      onChange={(e) => handleInputChange('tipoRecorrencia', e.target.value)}
                    >
                      <MenuItem value="semanal">Semanal</MenuItem>
                      <MenuItem value="quinzenal">Quinzenal</MenuItem>
                      <MenuItem value="mensal">Mensal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Intervalo"
                    type="number"
                    value={formData.intervaloRecorrencia}
                    onChange={(e) => handleInputChange('intervaloRecorrencia', e.target.value)}
                    helperText={
                      formData.tipoRecorrencia === 'semanal' ? 'A cada X semanas' :
                      formData.tipoRecorrencia === 'quinzenal' ? 'A cada X quinzenas' :
                      'A cada X meses'
                    }
                    inputProps={{ min: 1, max: 12 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.diaSemanaRecorrente}>
                    <InputLabel>Dia da semana</InputLabel>
                    <Select
                      value={formData.diaSemanaRecorrente || ''}
                      label="Dia da semana"
                      onChange={(e) => handleInputChange('diaSemanaRecorrente', e.target.value)}
                    >
                      <MenuItem value="segunda">Segunda-feira</MenuItem>
                      <MenuItem value="terca">Terça-feira</MenuItem>
                      <MenuItem value="quarta">Quarta-feira</MenuItem>
                      <MenuItem value="quinta">Quinta-feira</MenuItem>
                      <MenuItem value="sexta">Sexta-feira</MenuItem>
                      <MenuItem value="sabado">Sábado</MenuItem>
                      <MenuItem value="domingo">Domingo</MenuItem>
                    </Select>
                    {formErrors.diaSemanaRecorrente && (
                      <Typography variant="caption" color="error">
                        {formErrors.diaSemanaRecorrente}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Horário"
                    type="time"
                    value={formData.horarioRecorrente || ''}
                    onChange={(e) => handleInputChange('horarioRecorrente', e.target.value)}
                    error={!!formErrors.horarioRecorrente}
                    helperText={formErrors.horarioRecorrente}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Duração (minutos)"
                    type="number"
                    value={formData.duracaoRecorrente}
                    onChange={(e) => handleInputChange('duracaoRecorrente', parseInt(e.target.value))}
                    inputProps={{ min: 10, max: 180 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Consulta</InputLabel>
                    <Select
                      value={formData.tipo}
                      label="Tipo de Consulta"
                      onChange={(e) => handleInputChange('tipo', e.target.value)}
                    >
                      <MenuItem value="Presencial">Presencial</MenuItem>
                      <MenuItem value="Online">Online</MenuItem>
                      <MenuItem value="Domiciliar">Domiciliar</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Plataforma (se online)"
                    value={formData.plataforma || ''}
                    onChange={(e) => handleInputChange('plataforma', e.target.value)}
                    disabled={formData.tipo !== 'Online'}
                    error={!!formErrors.plataforma}
                    helperText={formErrors.plataforma}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data limite para recorrência (opcional)"
                    type="date"
                    value={formData.dataFimRecorrencia || ''}
                    onChange={(e) => handleInputChange('dataFimRecorrencia', e.target.value)}
                    helperText="Se não informado, serão criadas consultas para os próximos 6 meses"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    multiline
                    rows={2}
                    value={formData.observacoes || ''}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {formData.tipoRecorrencia === 'semanal' && 'Isto criará consultas recorrentes semanais no dia da semana e horário especificados. Dias bloqueados serão automaticamente ignorados.'}
                    {formData.tipoRecorrencia === 'quinzenal' && 'Isto criará consultas recorrentes a cada duas semanas no dia da semana e horário especificados. Dias bloqueados serão automaticamente ignorados.'}
                    {formData.tipoRecorrencia === 'mensal' && 'Isto criará consultas recorrentes mensais na mesma data do mês e horário especificados. Dias bloqueados serão automaticamente ignorados.'}
                  </Typography>
                </Grid>
              </>
            ) : (
              // Mantém o formulário existente para consultas normais
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!formErrors.pacienteId}>
                    <InputLabel>Paciente</InputLabel>
                    <Select
                      value={formData.pacienteId || ''}
                      label="Paciente"
                      onChange={(e) => handleInputChange('pacienteId', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Selecione um paciente</em>
                      </MenuItem>
                      {pacientes.map((paciente) => (
                        <MenuItem key={paciente.id} value={paciente.id.toString()}>
                          {paciente.nome}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.pacienteId && (
                      <Typography variant="caption" color="error">
                        {formErrors.pacienteId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="Agendada">Agendada</MenuItem>
                      <MenuItem value="Realizada">Realizada</MenuItem>
                      <MenuItem value="Cancelada">Cancelada</MenuItem>
                      <MenuItem value="Remarcada">Remarcada</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data"
                    type="date"
                    value={formData.start ? format(formData.start, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      const newDate = parse(e.target.value, 'yyyy-MM-dd', new Date());
                      const newStart = new Date(
                        newDate.getFullYear(),
                        newDate.getMonth(),
                        newDate.getDate(),
                        formData.start ? formData.start.getHours() : new Date().getHours(),
                        formData.start ? formData.start.getMinutes() : 0
                      );
                      const newEnd = addMinutes(newStart, duracaoPadrao);
                      
                      handleInputChange('start', newStart);
                      handleInputChange('end', newEnd);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Hora início"
                    type="time"
                    value={formData.start ? format(formData.start, 'HH:mm') : ''}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(':');
                      const newStart = new Date(formData.start);
                      newStart.setHours(parseInt(hours));
                      newStart.setMinutes(parseInt(minutes));
                      
                      const newEnd = addMinutes(newStart, duracaoPadrao);
                      
                      handleInputChange('start', newStart);
                      handleInputChange('end', newEnd);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Duração (min)"
                    type="number"
                    value={(formData.end && formData.start) ? 
                      (formData.end.getTime() - formData.start.getTime()) / 60000 : duracaoPadrao}
                    onChange={(e) => {
                      const newDuration = parseInt(e.target.value);
                      const newEnd = addMinutes(formData.start, newDuration);
                      handleInputChange('end', newEnd);
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Consulta</InputLabel>
                    <Select
                      value={formData.tipo}
                      label="Tipo de Consulta"
                      onChange={(e) => handleInputChange('tipo', e.target.value)}
                    >
                      <MenuItem value="Presencial">Presencial</MenuItem>
                      <MenuItem value="Online">Online</MenuItem>
                      <MenuItem value="Domiciliar">Domiciliar</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Plataforma (se online)"
                    value={formData.plataforma || ''}
                    onChange={(e) => handleInputChange('plataforma', e.target.value)}
                    disabled={formData.tipo !== 'Online'}
                    error={!!formErrors.plataforma}
                    helperText={formErrors.plataforma}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    multiline
                    rows={4}
                    value={formData.observacoes || ''}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          {currentEvent && (
            <Button 
              onClick={handleDeleteEvent} 
              color="error"
              sx={{ mr: 'auto' }}
            >
              Excluir
            </Button>
          )}
          <Button onClick={handleCloseEventDialog}>Cancelar</Button>
          <Button 
            onClick={formData.isRecorrente ? handleSaveRecorrente : handleSaveEvent} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            {formData.isRecorrente ? 'Criar Consultas Recorrentes' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para configurações */}
      <Dialog open={openConfigDialog} onClose={handleCloseConfigDialog} maxWidth="md" fullWidth>
        <DialogTitle>Configurações da Agenda</DialogTitle>
        
        <DialogContent>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{ mb: 3 }}
          >
            <Tab label="Horários de Atendimento" />
            <Tab label="Dias Bloqueados" />
            <Tab label="Preferências" />
          </Tabs>
          
          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Horários de funcionamento
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Configure os dias e horários em que você atende pacientes.
                </Typography>
              </Grid>
              
              {Object.entries(horariosFuncionamento).map(([dia, horario]) => (
                <Grid item xs={12} key={dia}>
                  <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={horario.ativo}
                            onChange={(e) => handleDiaAtivoChange(dia, e.target.checked)}
                          />
                        }
                        label={translateDayName(dia)}
                      />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Hora início"
                          type="time"
                          value={horario.inicio}
                          onChange={(e) => handleHorarioChange(dia, 'inicio', e.target.value)}
                          disabled={!horario.ativo}
                          fullWidth
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Hora fim"
                          type="time"
                          value={horario.fim}
                          onChange={(e) => handleHorarioChange(dia, 'fim', e.target.value)}
                          disabled={!horario.ativo}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
          
          {tabValue === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Dias Bloqueados
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Adicione dias ou períodos que você não estará disponível (férias, feriados, etc.)
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Bloquear período (férias)
                </Typography>
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Data início"
                        type="date"
                        value={formData.periodoInicioBloqueioDia || ''}
                        onChange={(e) => handleInputChange('periodoInicioBloqueioDia', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Data fim"
                        type="date"
                        value={formData.periodoFimBloqueioDia || ''}
                        onChange={(e) => handleInputChange('periodoFimBloqueioDia', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4}>
                      <TextField
                        fullWidth
                        label="Motivo (opcional)"
                        value={formData.motivoBloqueioIntervalo || ''}
                        onChange={(e) => handleInputChange('motivoBloqueioIntervalo', e.target.value)}
                        placeholder="Ex: Férias, Congresso"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleAddPeriodoBloqueado}
                        disabled={!formData.periodoInicioBloqueioDia || !formData.periodoFimBloqueioDia}
                        sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
                      >
                        Bloquear Período
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Bloquear dia específico
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <TextField
                    label="Data para bloquear"
                    type="date"
                    value={formData.novoDiaBloqueado || ''}
                    onChange={(e) => handleInputChange('novoDiaBloqueado', e.target.value)}
                    sx={{ mr: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (formData.novoDiaBloqueado) {
                        handleAddDiaBloqueado(formData.novoDiaBloqueado);
                        handleInputChange('novoDiaBloqueado', '');
                      }
                    }}
                  >
                    Adicionar
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Dias bloqueados ({diasBloqueados.length})
                </Typography>
                {diasBloqueados.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {diasBloqueados
                      .sort((a, b) => new Date(a) - new Date(b))
                      .map(dia => (
                        <Chip
                          key={dia}
                          label={format(new Date(dia), 'dd/MM/yyyy')}
                          onDelete={() => handleRemoveDiaBloqueado(dia)}
                          color="primary"
                          sx={{ bgcolor: '#4a8a84' }}
                        />
                      ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum dia bloqueado adicionado.
                  </Typography>
                )}
                
                {diasBloqueados.length > 0 && (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      if (window.confirm('Tem certeza que deseja remover todos os dias bloqueados?')) {
                        setDiasBloqueados([]);
                      }
                    }}
                  >
                    Limpar Todos
                  </Button>
                )}
              </Grid>
            </Grid>
          )}
          
          {tabValue === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Preferências
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duração padrão das consultas (minutos)"
                  type="number"
                  value={duracaoPadrao}
                  onChange={(e) => setDuracaoPadrao(parseInt(e.target.value))}
                  inputProps={{ min: 10, max: 180 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.permitirAgendamentoPacientes || false}
                      onChange={(e) => handleInputChange('permitirAgendamentoPacientes', e.target.checked)}
                    />
                  }
                  label="Permitir que pacientes agendem consultas diretamente"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enviarLembretesEmail || false}
                      onChange={(e) => handleInputChange('enviarLembretesEmail', e.target.checked)}
                    />
                  }
                  label="Enviar lembretes de consulta por e-mail"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseConfigDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveConfig} 
            variant="contained"
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
          >
            Salvar Configurações
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaPsicologo; 